import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckIcon,
  ExternalLinkIcon,
  PencilIcon,
  RefreshIcon,
  StarIcon,
  TrashIcon,
  XIcon,
} from "@heroicons/react/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import VSolonCard from "../../components/Cards/VSolonCard";
import useCaseDetail, { ASTIKA_ENDPOINTS } from "../../features/cases/useCaseDetail";
import { isCheckActive } from "../../features/cases/caseFilters";
import { truncateCourt } from "../../utils/truncateCourt";
import { caseDocumentTitle } from "../../components/SingleCase/ViewCase";
import PanelErrorBoundary from "./PanelErrorBoundary";

const isFav = (c) => c?.fav === 1 || c?.fav === true;

// Αστικά defaults; other case types pass their own through the page config.
const astikaTitle = (c) => (c?.note?.trim() ? c.note : c ? `ΓΑΚ ${c.gak}/${c.year}` : "");
const astikaSubtitle = (c) =>
  c
    ? [
        c.note?.trim() ? `ΓΑΚ ${c.gak}/${c.year}` : null,
        c.court ? truncateCourt(c.court) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
const astikaRefreshKey = (c) => `${c?.last_checked ?? ""}|${c?.check_status?.state ?? ""}`;
const astikaBody = ({ data, updates, history, historyError }) => (
  <VSolonCard
    key={data.id}
    variant="panel"
    singleCase={data}
    singleUpdates={updates}
    historyData={history}
    historyError={historyError}
    historyLoading={history === undefined}
  />
);

const NoteEditor = ({ initial, onSave, onDone }) => {
  const [value, setValue] = useState(initial);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = async () => {
    const ok = await onSave(value.trim());
    if (ok !== false) onDone();
  };

  return (
    <form
      className="ds-note-editor"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // Handled here so the page's Esc (close panel) does not run.
            e.preventDefault();
            e.stopPropagation();
            onDone();
          }
        }}
        aria-label="Σημείωση"
        placeholder="Σημείωση"
        maxLength={100}
      />
      <button type="submit" className="ds-icon-btn" aria-label="Αποθήκευση" title="Αποθήκευση ( Enter )">
        <CheckIcon />
      </button>
      <button type="button" className="ds-icon-btn" onClick={onDone} aria-label="Ακύρωση" title="Ακύρωση ( Esc )">
        <XIcon />
      </button>
    </form>
  );
};

// Case details rendered next to the table. `rowCase` is the list entry: it
// fills the header immediately and its check state drives refreshes. With
// `endpoints={null}` nothing is fetched and the body renders the row itself.
// Header actions render only for the handlers that are passed.
const CaseDetailPanel = ({
  caseId,
  rowCase,
  pendingCheck,
  onClose,
  onToggleFav,
  onRecheck,
  onDelete,
  onSaveNote,
  endpoints = ASTIKA_ENDPOINTS,
  getTitle = astikaTitle,
  getSubtitle = astikaSubtitle,
  getRefreshKey = astikaRefreshKey,
  fullPageHref = (id) => `/cases/${id}`,
  documentTitle = caseDocumentTitle,
  renderBody = astikaBody,
  ariaLabel = "Στοιχεία υπόθεσης",
}) => {
  const detail = useCaseDetail(endpoints ? caseId : null, {
    refreshKey: getRefreshKey(rowCase),
    endpoints: endpoints ?? ASTIKA_ENDPOINTS,
  });
  const { updates, history, historyError, setData } = detail;
  const data = endpoints ? detail.data : rowCase;
  const loading = endpoints ? detail.loading : false;
  const error = endpoints ? detail.error : null;
  const [editingNote, setEditingNote] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    setEditingNote(false);
  }, [caseId]);

  useEffect(() => {
    if (data && documentTitle) document.title = documentTitle(data);
  }, [data, documentTitle]);

  const headerCase = rowCase ?? data;
  const checking = pendingCheck || isCheckActive(rowCase);
  const title = getTitle(headerCase);
  const subtitle = getSubtitle(headerCase);
  const href = fullPageHref ? fullPageHref(caseId) : null;

  return (
    <section className="ds-panel" aria-label={ariaLabel}>
      <div className="ds-panel-header">
        <div className="ds-panel-heading">
          {editingNote ? (
            <NoteEditor
              initial={headerCase?.note ?? ""}
              onSave={(note) => onSaveNote(caseId, note)}
              onDone={() => setEditingNote(false)}
            />
          ) : (
            <>
              <div className="ds-panel-title" title={title}>
                {title}
              </div>
              {subtitle && <div className="ds-panel-subtitle">{subtitle}</div>}
            </>
          )}
        </div>
        <div className="ds-panel-actions">
          {headerCase && !editingNote && (
            <>
              {onSaveNote && (
                <button
                  type="button"
                  className="ds-icon-btn"
                  onClick={() => setEditingNote(true)}
                  aria-label="Επεξεργασία σημείωσης"
                  title="Επεξεργασία σημείωσης"
                >
                  <PencilIcon />
                </button>
              )}
              {onToggleFav && (
                <button
                  type="button"
                  className={`ds-icon-btn ds-star ${isFav(headerCase) ? "is-fav" : ""}`}
                  onClick={() => onToggleFav(caseId)}
                  aria-pressed={isFav(headerCase)}
                  aria-label="Σημαντικό"
                  title="Σημαντικό ( s )"
                >
                  {isFav(headerCase) ? <StarIconSolid /> : <StarIcon />}
                </button>
              )}
              {onRecheck && (
                <button
                  type="button"
                  className="ds-icon-btn"
                  onClick={() => onRecheck(caseId)}
                  disabled={checking}
                  aria-label="Έλεγχος τώρα"
                  title={checking ? "Γίνεται έλεγχος…" : "Έλεγχος τώρα ( r )"}
                >
                  <RefreshIcon className={checking ? "ds-spin" : ""} />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="ds-icon-btn ds-icon-btn--danger"
                  onClick={() => onDelete(caseId)}
                  aria-label="Διαγραφή"
                  title="Διαγραφή"
                >
                  <TrashIcon />
                </button>
              )}
            </>
          )}
          {href && (
            <Link
              to={href}
              className="ds-icon-btn"
              aria-label="Πλήρης σελίδα"
              title="Πλήρης σελίδα ( o )"
            >
              <ExternalLinkIcon />
            </Link>
          )}
          <button
            type="button"
            className="ds-icon-btn"
            onClick={onClose}
            aria-label="Κλείσιμο"
            title="Κλείσιμο ( Esc )"
          >
            <XIcon />
          </button>
        </div>
      </div>
      <div className="ds-panel-body" ref={bodyRef}>
        {loading ? (
          <div className="ds-panel-placeholder">
            <span className="ds-spinner" aria-hidden="true" /> Ανακτώνται τα δεδομένα…
          </div>
        ) : error ? (
          <div className="ds-panel-placeholder ds-panel-error">
            {error?.response?.status === 404
              ? "Δεν βρέθηκε η εγγραφή."
              : "Δεν ήταν δυνατή η φόρτωση των στοιχείων."}
          </div>
        ) : data ? (
          <PanelErrorBoundary
            key={caseId}
            fallback={
              href ? (
                <Link to={href} className="ds-link-btn">
                  Άνοιγμα σε πλήρη σελίδα
                </Link>
              ) : null
            }
          >
            {renderBody({ data, updates, history, historyError, setData, rowCase })}
          </PanelErrorBoundary>
        ) : (
          <div className="ds-panel-placeholder">Δεν βρέθηκε η εγγραφή.</div>
        )}
      </div>
    </section>
  );
};

export default CaseDetailPanel;
